import { useEffect, useRef, useState } from 'react';
import { apiClient } from '../services/authService';

const normalizePath = (path = '') => {
  if (!path) {
    return '/';
  }
  const normalized = `/${String(path).replace(/^\/+/, '')}`;
  return normalized.replace(/\/+$/, '') || '/';
};

const shouldUseDirectUrl = (assetUrl, responseType = 'blob') => {
  if (!assetUrl || responseType === 'text') {
    return false;
  }

  const raw = String(assetUrl);
  if (raw.startsWith('blob:') || raw.startsWith('data:')) {
    return true;
  }

  if (!/^https?:\/\//i.test(raw)) {
    return false;
  }

  try {
    const asset = new URL(raw);
    const base = new URL(
      apiClient.defaults.baseURL || '/api/v1',
      typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
    );
    const apiPath = normalizePath(base.pathname);
    const assetPath = normalizePath(asset.pathname);
    const isApiOrigin = asset.origin === base.origin;
    const isApiPath = assetPath === apiPath || assetPath.startsWith(`${apiPath}/`);
    return !(isApiOrigin && isApiPath);
  } catch (_error) {
    return false;
  }
};

export const useAuthObjectUrl = (assetUrl, { responseType = 'blob' } = {}) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const activeUrlRef = useRef(null);

  useEffect(() => {
    if (!assetUrl) {
      if (activeUrlRef.current) {
        URL.revokeObjectURL(activeUrlRef.current);
        activeUrlRef.current = null;
      }
      setData(null);
      setError(null);
      return undefined;
    }

    let isCancelled = false;
    let createdUrl = null;

    const fetchAsset = async () => {
      try {
        setError(null);
        const isText = responseType === 'text';
        const isDirectUrl = shouldUseDirectUrl(assetUrl, responseType);
        const rawUrl = String(assetUrl);
        const isAlreadyLocalObjectUrl =
          rawUrl.startsWith('blob:') || rawUrl.startsWith('data:');

        let payload;
        if (isAlreadyLocalObjectUrl && !isText) {
          payload = rawUrl;
        } else if (isDirectUrl) {
          const directResponse = await fetch(assetUrl, {
            method: 'GET',
            credentials: 'omit',
            headers: { Accept: isText ? 'text/plain' : 'application/octet-stream' },
          });
          if (!directResponse.ok) {
            throw new Error(`Asset request failed with status ${directResponse.status}`);
          }
          if (isText) {
            payload = await directResponse.text();
          } else {
            const blob = await directResponse.blob();
            createdUrl = URL.createObjectURL(blob);
            payload = createdUrl;
          }
        } else {
          const response = await apiClient.get(assetUrl, {
            responseType: isText ? 'text' : 'blob',
            headers: { Accept: isText ? 'text/plain' : 'application/octet-stream' },
          });

          if (isText) {
            payload = response.data;
          } else {
            const blob = response.data;
            createdUrl = URL.createObjectURL(blob);
            payload = createdUrl;
          }
        }

        if (isCancelled) {
          if (createdUrl) {
            URL.revokeObjectURL(createdUrl);
          }
          return;
        }

        if (responseType === 'text') {
          setData(payload);
        } else {
          if (activeUrlRef.current) {
            URL.revokeObjectURL(activeUrlRef.current);
          }
          activeUrlRef.current = payload;
          setData(payload);
        }
      } catch (err) {
        if (isCancelled) {
          return;
        }
        setError(err instanceof Error ? err : new Error('Failed to load asset'));
        setData(null);
      }
    };

    fetchAsset();

    return () => {
      isCancelled = true;
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
    };
  }, [assetUrl, responseType]);

  useEffect(() => {
    return () => {
      if (activeUrlRef.current) {
        URL.revokeObjectURL(activeUrlRef.current);
        activeUrlRef.current = null;
      }
    };
  }, []);

  return { data, error };
};

export default useAuthObjectUrl;
